/**
 * Section sub-tabs.
 *
 * The bottom nav has four destinations, but two of them span more than one
 * page: Voir Dire covers the juror roster and the question bank, Reference
 * covers Legal Docs and Law. Rather than merge those pages — which would mean
 * moving a lot of markup and rewiring their render hooks — each section gets a
 * sub-tab row above the page area, and the router keeps switching pages exactly
 * as before.
 *
 * Consequences worth knowing:
 *   - every existing showPage() call site keeps working untouched
 *   - page ids, element ids and per-page enter hooks are unchanged
 *   - the row is empty (and hidden) for single-page sections like Scan
 */

import { byId } from './dom';

interface Section {
  /** id of the bottom-nav item to light up for this page */
  nav: string;
  /** [label, target page id] — omitted for single-page sections */
  tabs?: [string, string][];
}

const VOIR_DIRE: [string, string][] = [
  ['Individual', 'panel'],
  ['General', 'vd']
];

const REFERENCE: [string, string][] = [
  ['Documents', 'legal'],
  ['Law', 'law']
];

/** Which bottom-nav item owns each page, and what sits in its sub-tab row. */
const SECTIONS: Record<string, Section> = {
  case: { nav: 'case' },
  scan: { nav: 'scan' },
  panel: { nav: 'vd', tabs: VOIR_DIRE },
  vd: { nav: 'vd', tabs: VOIR_DIRE },
  legal: { nav: 'reference', tabs: REFERENCE },
  law: { nav: 'reference', tabs: REFERENCE },
  // Research is reachable from Law but is not itself a nav destination.
  research: { nav: 'reference', tabs: REFERENCE }
};

/** The bottom-nav id that should be active while `pageId` is showing. */
export function navIdFor(pageId: string): string {
  return SECTIONS[pageId]?.nav ?? pageId;
}

/** Render the sub-tab row for whichever section `pageId` belongs to. */
export function renderSectionTabs(pageId: string): void {
  const host = byId('section-tabs');
  if (!host) return;

  const tabs = SECTIONS[pageId]?.tabs;
  if (!tabs) {
    host.innerHTML = '';
    return;
  }

  host.innerHTML = tabs
    .map(
      ([label, target]) =>
        `<button class="case-tab${target === pageId ? ' active' : ''}" ` +
        `onclick="showPage('${target}')">${label}</button>`
    )
    .join('');
}
